import api from '../request'
import { API_BASE_URL, ORG_ENDPOINTS } from '../../const/EndPoints'
import {
  Organisation,
  OrganisationMember,
  OrgOverview,
  OrgEngagementSummary,
  OrgBilling,
  OrgSettings,
} from '../../models/Organisation'

// ─── SSO config (per 034-enterprise-sso.md) ───

export interface OrgSSOConfig {
  sso_enabled: boolean
  provider_type: 'saml' | 'oidc' | null
  org_name: string
  logo_url: string | null
  slug: string
}

// GET /v1/org/{slug}/sso/config — public, no auth required
export const getOrgSSOConfig = async (slug: string): Promise<OrgSSOConfig> => {
  const response = await api.get<OrgSSOConfig>(ORG_ENDPOINTS.SSO_CONFIG(slug))
  return response.data
}

// ─── SSO login redirects (all go to identity service which 302s to Cognito) ───

// Company SSO (SAML/OIDC) — identity service builds Cognito authorize URL
// with identity_provider hint, 302 redirects to corporate IdP
export const initiateSSO = (slug: string): void => {
  window.location.assign(API_BASE_URL + ORG_ENDPOINTS.SSO_LOGIN(slug))
}

// Federated SSO — Google / Microsoft via org context
// Identity service routes to the correct Cognito IdP with org awareness
export const initiateFederatedSSO = (slug: string, provider: 'google' | 'microsoft'): void => {
  window.location.assign(API_BASE_URL + ORG_ENDPOINTS.SSO_LOGIN_PROVIDER(slug, provider))
}

// ─── Email domain lookup (public — no auth) ───

export interface DomainLookupResult {
  match: boolean
  slug?: string
  orgName?: string
}

// GET /v1/org/lookup?domain=citi.com → check if email domain belongs to an org
export const lookupEmailDomain = async (domain: string): Promise<DomainLookupResult> => {
  try {
    const response = await api.get<DomainLookupResult>(ORG_ENDPOINTS.DOMAIN_LOOKUP(domain))
    return response.data
  } catch {
    return { match: false }
  }
}

// ─── Current user's org membership (protected — Cognito JWT) ───

export interface OrgMembership {
  organisationId: string
  organisationName: string
  organisationSlug: string
  role: 'admin' | 'member'
  plan: string
}

// GET /v1/org/me → current user's org membership (null if not in an org)
export const getMyOrg = async (): Promise<OrgMembership | null> => {
  try {
    const response = await api.get<OrgMembership>(ORG_ENDPOINTS.ME)
    return response.data
  } catch {
    return null // User is not in an organisation
  }
}

// ─── Invite acceptance ───

export const acceptInvite = async (
  token: string,
  credentials?: { email: string; password: string; name?: string }
): Promise<{ userId: string; accessToken: string }> => {
  const response = await api.post(ORG_ENDPOINTS.ACCEPT_INVITE(token), credentials)
  return response.data
}

// ─── Admin: Dashboard overview ───

export const getOrgDashboard = async (slug: string): Promise<OrgOverview> => {
  const response = await api.get<OrgOverview>(ORG_ENDPOINTS.DASHBOARD(slug))
  return response.data
}

// ─── Admin: Members ───

export const getOrgMembers = async (
  slug: string,
  params?: { page?: number; pageSize?: number; status?: string; search?: string }
): Promise<{ members: OrganisationMember[]; total: number }> => {
  const response = await api.get(ORG_ENDPOINTS.MEMBERS(slug), { params })
  return response.data
}

export const inviteMember = async (
  slug: string,
  email: string
): Promise<{ message: string }> => {
  const response = await api.post(ORG_ENDPOINTS.INVITE(slug), { email })
  return response.data
}

export const bulkInviteMembers = async (
  slug: string,
  emails: string[]
): Promise<{ invited: number; failed: number; errors?: string[] }> => {
  const response = await api.post(ORG_ENDPOINTS.BULK_INVITE(slug), { emails })
  return response.data
}

export const csvInviteMembers = async (
  slug: string,
  file: File
): Promise<{ invited: number; failed: number; errors?: string[] }> => {
  const formData = new FormData()
  formData.append('file', file)
  const response = await api.post(ORG_ENDPOINTS.CSV_INVITE(slug), formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return response.data
}

export const deactivateMember = async (
  slug: string,
  userId: string
): Promise<{ message: string }> => {
  const response = await api.delete(ORG_ENDPOINTS.DEACTIVATE_MEMBER(slug, userId))
  return response.data
}

// ─── Admin: Engagement ───

export const getOrgEngagement = async (
  slug: string,
  period: '7d' | '30d' | '90d' = '30d'
): Promise<OrgEngagementSummary> => {
  const response = await api.get<OrgEngagementSummary>(
    ORG_ENDPOINTS.ENGAGEMENT(slug),
    { params: { period } }
  )
  return response.data
}

export const exportEngagementReport = async (slug: string): Promise<Blob> => {
  const response = await api.get(ORG_ENDPOINTS.ENGAGEMENT_EXPORT(slug), {
    responseType: 'blob',
  })
  return response.data
}

// ─── Admin: Billing ───

export const getOrgBilling = async (slug: string): Promise<OrgBilling> => {
  const response = await api.get<OrgBilling>(ORG_ENDPOINTS.BILLING(slug))
  return response.data
}

// ─── Admin: Settings ───

export const getOrgSettings = async (slug: string): Promise<OrgSettings> => {
  const response = await api.get<OrgSettings>(ORG_ENDPOINTS.SETTINGS(slug))
  return response.data
}

export const updateOrgSettings = async (
  slug: string,
  data: Partial<OrgSettings>
): Promise<OrgSettings> => {
  const response = await api.put<OrgSettings>(ORG_ENDPOINTS.SETTINGS(slug), data)
  return response.data
}

export const updateOrgLogo = async (
  slug: string,
  file: File
): Promise<{ logoUrl: string }> => {
  const formData = new FormData()
  formData.append('logo', file)
  const response = await api.put(ORG_ENDPOINTS.SETTINGS(slug), formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return response.data
}
